import { fetchPostsFromWordPressApi } from "@/utils/wordpress/api";
import { BlogPost } from "@/types/blog";
import { processImage } from "./imageProcessing";
import { htmlToMarkdown, isHtmlContent } from "./editorConversion";
import { toast } from "@/hooks/use-toast";

/**
 * Escapes special characters for use in a regular expression
 * @param string String to escape
 * @returns Escaped string safe for regex
 */
const escapeRegExp = (string: string): string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
};

/**
 * Generates markdown files from WordPress posts
 * This is a browser-compatible version that returns the blog posts
 * but doesn't write files (that happens in the UI)
 * 
 * @param wpApiUrl The WordPress API URL (e.g., https://edgefi.com/wp-json/wp/v2/posts)
 * @returns Promise resolving to an array of BlogPost objects
 */
export const generateMarkdownFilesFromWordPress = async (
  wpApiUrl: string
): Promise<BlogPost[]> => {
  try {
    // Fetch posts from WordPress API
    console.log(`Fetching posts from ${wpApiUrl}...`);
    const posts = await fetchPostsFromWordPressApi(wpApiUrl);
    console.log(`Fetched ${posts.length} posts.`);
    
    // Process posts to ensure content is properly formatted
    const processedPosts = await Promise.all(posts.map(async (post) => {
      // Process featured image if it exists
      if (post.coverImage) {
        try {
          console.log(`Processing featured image for post: ${post.title}`);
          const localImage = await processImage(post.coverImage, post.slug);
          post.coverImage = localImage;
        } catch (imageError) {
          console.error(`Error processing featured image for ${post.title}:`, imageError);
          // Keep the original image URL if processing fails
        }
      }
      
      // Filter out "Uncategorized" tags
      if (post.tags && Array.isArray(post.tags)) {
        post.tags = post.tags.filter(tag => 
          tag && tag.trim() !== "" && tag.toLowerCase() !== "uncategorized"
        );
      }
      
      // Process content to extract and download embedded images
      post.content = await processContentImages(post.content, post.slug);
      
      // Process embedded videos in content
      post.content = processEmbeddedVideos(post.content);
      
      // Convert HTML content to markdown if needed
      if (post.content && isHtmlContent(post.content)) {
        try {
          console.log(`Converting HTML content to markdown for post: ${post.title}`);
          post.content = await htmlToMarkdown(post.content);
        } catch (error) {
          console.error(`Error converting HTML to markdown for ${post.title}:`, error);
          // Keep the original content if conversion fails
          post.content = processWordPressContent(post.content);
        }
      }
      
      return post;
    }));
    
    return processedPosts;
  } catch (error) {
    console.error("Failed to generate markdown files:", error);
    throw error;
  }
};

/**
 * Process embedded videos in content
 * @param content HTML content
 * @returns Processed content with proper video embeds
 */
export const processEmbeddedVideos = (content: string): string => {
  if (!content) return '';
  
  let processedContent = content;
  
  // Find YouTube shortlinks and convert to full URLs
  processedContent = processedContent.replace(
    /https?:\/\/youtu\.be\/([a-zA-Z0-9_-]+)/g, 
    'https://www.youtube.com/watch?v=$1'
  );
  
  // Find YouTube URLs and convert to embeds
  processedContent = processedContent.replace(
    /(?:^|\s|>)(https?:\/\/(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+))(?:&[^<\s]*)?(?:<|\s|$)/g,
    (match, url, videoId) => {
      return `<iframe width="560" height="315" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen loading="lazy"></iframe>`;
    }
  );
  
  // Find Vimeo URLs and convert to embeds
  processedContent = processedContent.replace(
    /(?:^|\s|>)(https?:\/\/(?:www\.)?vimeo\.com\/([0-9]+))(?:<|\s|$)/g,
    (match, url, videoId) => {
      return `<iframe src="https://player.vimeo.com/video/${videoId}" width="640" height="360" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen loading="lazy"></iframe>`;
    }
  );
  
  // Handle WordPress [embed] shortcodes
  processedContent = processedContent.replace(
    /\[embed.*?\](https?:\/\/[^\s\]]+)\[\/embed\]/g,
    (match, url) => {
      // YouTube
      const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\?]+)/);
      if (ytMatch) {
        return `<iframe width="560" height="315" src="https://www.youtube.com/embed/${ytMatch[1]}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen loading="lazy"></iframe>`;
      }
      
      // Vimeo
      const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
      if (vimeoMatch) {
        return `<iframe src="https://player.vimeo.com/video/${vimeoMatch[1]}" width="640" height="360" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen loading="lazy"></iframe>`;
      }
      
      // Default to linked URL
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    }
  );
  
  // Handle WordPress [video] shortcodes
  processedContent = processedContent.replace(
    /\[video.*?\](.*?)\[\/video\]/g,
    (match, content) => {
      const urlMatch = content.match(/(https?:\/\/[^\s]+)/);
      if (urlMatch) {
        // YouTube
        const ytMatch = urlMatch[1].match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\?]+)/);
        if (ytMatch) {
          return `<iframe width="560" height="315" src="https://www.youtube.com/embed/${ytMatch[1]}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen loading="lazy"></iframe>`;
        }
        
        // Vimeo
        const vimeoMatch = urlMatch[1].match(/vimeo\.com\/(\d+)/);
        if (vimeoMatch) {
          return `<iframe src="https://player.vimeo.com/video/${vimeoMatch[1]}" width="640" height="360" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen loading="lazy"></iframe>`;
        }
        
        // Generic video
        return `<video controls src="${urlMatch[1]}" width="100%" loading="lazy"></video>`;
      }
      
      return match; // Return original if no URL found
    }
  );
  
  return processedContent;
};

/**
 * Processes HTML content to download and replace image URLs with local paths
 * @param content HTML content
 * @param slug Post slug for naming convention
 * @returns Processed content with updated image URLs
 */
export const processContentImages = async (content: string, slug: string): Promise<string> => {
  if (!content) return '';
  
  // Find all image tags with more comprehensive regex that handles WordPress-specific attributes
  const imgRegex = /<img[\s\S]*?src="([^">]+)"[\s\S]*?>/g;
  let match;
  let processedContent = content;
  let imageCounter = 0;
  let downloadPromises = [];
  
  // First, find all the images and collect info to process them
  const imagesToProcess = [];
  while ((match = imgRegex.exec(content)) !== null) {
    const originalUrl = match[1];
    const fullImgTag = match[0];
    
    // Skip already downloaded images, data URLs or SVG images
    if (originalUrl.startsWith('/lovable-uploads/') || 
        originalUrl.startsWith('data:') || 
        originalUrl.endsWith('.svg')) {
      continue;
    }
    
    // Extract alt text if available
    const altMatch = fullImgTag.match(/alt="([^"]*)"/) || [];
    const altText = altMatch[1] || `${slug}-image-${imageCounter}`;
    
    // Extract title/caption if available
    const titleMatch = fullImgTag.match(/title="([^"]*)"/) || [];
    const caption = titleMatch[1] || '';
    
    imagesToProcess.push({
      originalUrl,
      fullImgTag,
      altText,
      caption,
      index: imageCounter++
    });
  }
  
  // Process each image in parallel
  const imageMap = new Map(); // Map from original URL to new URL
  await Promise.all(
    imagesToProcess.map(async (img) => {
      try {
        const localUrl = await processImage(img.originalUrl, `${slug}-content-${img.index}`);
        imageMap.set(img.originalUrl, localUrl);
        console.log(`Processed image: ${img.originalUrl} -> ${localUrl}`);
      } catch (error) {
        console.error(`Error processing image ${img.originalUrl}:`, error);
        // Keep original URL if processing fails
      }
    })
  );
  
  // Now replace all URLs in the content
  for (const [originalUrl, localUrl] of imageMap.entries()) {
    // Replace all instances of this URL in the content using a global regex instead of replaceAll
    const safeOriginalUrl = escapeRegExp(originalUrl);
    processedContent = processedContent.replace(new RegExp(safeOriginalUrl, 'g'), localUrl);
  }
  
  // Also find WordPress gallery images if present
  const galleryRegex = /\[gallery[^\]]*ids="([^"]+)"[^\]]*\]/g;
  while ((match = galleryRegex.exec(content)) !== null) {
    const ids = match[1].split(',');
    // Replace gallery shortcode with placeholder text
    processedContent = processedContent.replace(
      match[0], 
      '<p><em>This post contains a gallery that has been imported as individual images.</em></p>'
    );
  }
  
  // Process background images in divs and sections
  const bgImageRegex = /background-image:[\s\S]*?url\(['"]?([^'"\)]+)['"]?\)/g;
  while ((match = bgImageRegex.exec(content)) !== null) {
    const originalUrl = match[1];
    
    // Skip already downloaded images or data URLs
    if (originalUrl.startsWith('/lovable-uploads/') || 
        originalUrl.startsWith('data:')) {
      continue;
    }
    
    try {
      const localUrl = await processImage(originalUrl, `${slug}-bg-${imageCounter++}`);
      // Replace this specific URL in the content
      processedContent = processedContent.replace(originalUrl, localUrl);
      console.log(`Processed background image: ${originalUrl} -> ${localUrl}`);
    } catch (error) {
      console.error(`Error processing background image ${originalUrl}:`, error);
      // Keep original URL if processing fails
    }
  }
  
  return processedContent;
};

/**
 * Converts a BlogPost object to markdown format
 * @param post The BlogPost object
 * @returns Markdown content as a string
 */
export const generateMarkdownFromPost = (post: BlogPost): string => {
  // Filter out unwanted tags
  let filteredTags = post.tags;
  if (filteredTags && Array.isArray(filteredTags)) {
    filteredTags = filteredTags.filter(tag => 
      tag && tag.trim() !== "" && tag.toLowerCase() !== "uncategorized"
    );
  }
  
  // Create frontmatter section with all available metadata
  const frontmatter = [
    '---',
    `title: ${escapeFrontmatter(post.title)}`,
    `date: ${post.date}`,
    post.author ? `author: ${escapeFrontmatter(post.author)}` : '',
    `slug: ${post.slug}`,
    `excerpt: ${escapeFrontmatter(post.excerpt)}`,
    post.coverImage ? `coverImage: ${post.coverImage}` : '',
    filteredTags?.length ? `tags: [${filteredTags.map(tag => `"${escapeFrontmatter(tag)}"`).join(', ')}]` : '',
    post.readingTime ? `readingTime: ${post.readingTime}` : '',
    post.metaDescription ? `metaDescription: ${escapeFrontmatter(post.metaDescription)}` : '',
    post.keywords?.length ? `keywords: [${post.keywords.map(kw => `"${escapeFrontmatter(kw)}"`).join(', ')}]` : '',
    post.wordpressId ? `wordpressId: "${post.wordpressId}"` : '',
    post.originalDate ? `originalDate: "${post.originalDate}"` : '',
    '---',
    ''
  ].filter(Boolean).join('\n');

  // Check if content needs conversion
  let processedContent = post.content;
  
  if (isHtmlContent(processedContent)) {
    // Even if already converted, process WordPress-specific issues
    processedContent = processWordPressContent(processedContent);
  }
  
  // Return the frontmatter and processed content
  return `${frontmatter}\n${processedContent}`;
};

/**
 * Processes WordPress content to improve markdown compatibility
 * @param content WordPress HTML content
 * @returns Processed content
 */
const processWordPressContent = (content: string): string => {
  if (!content) return '';
  
  let processedContent = content;
  
  // Replace WordPress shortcodes
  processedContent = processedContent.replace(/\[caption.*?\](.*?)\[\/caption\]/g, '$1');
  processedContent = processedContent.replace(/\[\/?[a-z0-9_-]+.*?\]/gi, '');
  
  // Strip script tags
  processedContent = processedContent.replace(/<script.*?>.*?<\/script>/gs, '');
  
  // Fix common WordPress formatting issues
  // Convert empty paragraphs to line breaks
  processedContent = processedContent.replace(/<p>\s*&nbsp;\s*<\/p>/g, '<br>');
  
  // Clean up WordPress-specific classes and styles
  processedContent = processedContent.replace(/class="[^"]*wp-block[^"]*"/g, '');
  processedContent = processedContent.replace(/style="[^"]*"/g, '');
  
  // Fix list formatting
  processedContent = processedContent.replace(/<ul [^>]*>/g, '<ul>');
  processedContent = processedContent.replace(/<ol [^>]*>/g, '<ol>');
  
  // Fix heading levels for proper hierarchy
  processedContent = processedContent.replace(/<h1/g, '<h2');
  
  // Handle WordPress galleries
  processedContent = processedContent.replace(/\[gallery.*?\]/g, '<div class="image-gallery">Gallery content - please check original post</div>');
  
  // Handle WordPress embeds
  processedContent = processedContent.replace(/\[embed.*?\](.*?)\[\/embed\]/g, '<div class="embed-container">$1</div>');
  
  // Remove excessive spaces
  processedContent = processedContent.replace(/\s{2,}/g, ' ');
  
  // Make sure all WordPress content is properly wrapped in paragraphs
  // This is important because sometimes WordPress exports have text nodes outside paragraphs
  if (!processedContent.startsWith('<') && processedContent.trim().length > 0) {
    processedContent = `<p>${processedContent}</p>`;
  }
  
  return processedContent;
};

/**
 * Escapes special characters for YAML frontmatter
 * @param text Text to escape
 * @returns Escaped text
 */
const escapeFrontmatter = (text: string): string => {
  if (!text) return '';
  
  // Clean up HTML entities
  let cleaned = text.replace(/&nbsp;/g, ' ')
                    .replace(/&amp;/g, '&')
                    .replace(/&lt;/g, '<')
                    .replace(/&gt;/g, '>')
                    .replace(/&quot;/g, '"')
                    .replace(/&#039;/g, "'");
                    
  // If text contains any special characters, wrap it in quotes
  if (/[:#\[\]\{\},"'&%]/.test(cleaned)) {
    // Escape any double quotes
    const escaped = cleaned.replace(/"/g, '\\"');
    return `"${escaped}"`;
  }
  
  return cleaned;
};
